import { intl } from "LocaleWrapper";
import z from "zod";

export const productSelectOptionSchema = z.object({
	id: z.number(),
	name: z.string(),
	code: z.string().nullish(),
	description: z.string().nullish(),
	densityKgM3: z.number().nullish(),
});

export const adnGoodSchema = z.object({
	tankerType: z.string(),
	cargoTankDesign: z.string(),
	cargoTankType: z.string(),
	cargoTankEquipment: z.array(z.string()).nullish(),
	samplingType: z.string(),
	temperatureClass: z.string().nullish(),
	hazardCode: z.string(),
	requiredEquipment: z.array(z.string()),
	openingPressureRelieveValve: z.string(),
	maxFillingDegreePercentage: z.string(),
	explosionGroup: z.string().nullish(),
});

export const productSchema = z.object({
	id: z.number().nullable(),
	name: z.string().min(1).max(50),
	code: z.string().optional(),
	externalId: z.string().max(50).optional(),
	abbreviation: z.string().max(16).optional(),
	category: z.object({ id: z.number(), name: z.string() }).nullable(),
	previousCargoDisplayName: z.string().optional(),
	casNumber: z.string().optional().refine(val => {
		if (val != null && val.trim().length !== 0) {
			return /^\d{2,7}-\d{2}-\d$/.test(val);
		}
		return true;
	}, { message: intl.formatMessage({ id: "PRODUCT_CAS_NUMBER_INVALID" }) }),
	volumeCorrectionConversionMethod: z.string().nullish(),
	densityCorrectionFactor: z.coerce.number().optional(),
	vaporPressure: z.coerce.number().optional(),
	vaporPressureMin: z.coerce.number().optional(),
	vaporPressureMax: z.coerce.number().optional(),
	vaporPressureTemperature: z.coerce.number().optional(),
	viscosity: z.coerce.number().optional(),
	viscosityMin: z.coerce.number().optional(),
	viscosityMax: z.coerce.number().optional(),
	flashPoint: z.coerce.number().optional(),
	flashPointMin: z.coerce.number().optional(),
	flashPointMax: z.coerce.number().optional(),
	meltingPoint: z.coerce.number().optional(),
	meltingPointMin: z.coerce.number().optional(),
	meltingPointMax: z.coerce.number().optional(),
	boilingPoint: z.coerce.number().optional(),
	boilingPointMin: z.coerce.number().optional(),
	boilingPointMax: z.coerce.number().optional(),
	autoIgnitionTemperature: z.coerce.number().optional(),
	autoIgnitionTemperatureMin: z.coerce.number().optional(),
	autoIgnitionTemperatureMax: z.coerce.number().optional(),
	selfAcceleratingDecompositionPolymerisationTemperature: z.coerce.number().optional(),
	maximumContactTemperature: z.coerce.number().optional(),
	lowerExplosiveLimit: z.coerce.number().optional(),
	upperExplosiveLimit: z.coerce.number().optional(),
	acceptedVentFreeLevel: z.coerce.number().optional(),
	productInstructionsForVessel: z.string().optional(),
	heatingInstructionsForVessel: z.string().optional(),
	description: z.string().optional(),
	remark: z.string().optional(),
	loadingType: z.string().optional(),
	densityKgM3: z.coerce.number().optional(),
	densityKgM3Min: z.coerce.number().optional(),
	densityKgM3Max: z.coerce.number().optional(),
	nstrCode: z.number().optional(),
	properShippingNameNL: z.string().min(1),
	properShippingNameEN: z.string().min(1),
	properShippingNameDE: z.string().min(1),
	properShippingNameFR: z.string().min(1),
	density: z.string().optional(),
	densityTemperature: z.coerce.number().optional(),
	customerId: z.coerce.number().optional(),
	productIDTFGoodId: z.number().optional(),
	productNSTRGoodId: z.number().optional(),
	productADNGoodId: z.number().optional(),
	adnGoodInput: adnGoodSchema.optional(),
	isDangerousGood: z.boolean(),
	isActive: z.boolean(),
	isValidated: z.boolean(),
}).superRefine((values, ctx): void => {
	const hasDensity =
		typeof values.density === "string"
			? values.density.trim().length > 0
			: Boolean(values.density);

	const tempMissing = values.densityTemperature === undefined;

	if (values.isDangerousGood && hasDensity) {
		if (tempMissing) {
			ctx.addIssue({
				path: [ "densityTemperature" ],
				code: z.ZodIssueCode.custom,
				message:
					"densityTemperature is required when density is provided for dangerous goods.",
			});
		}
	} else {
		if (values.densityKgM3 != null && tempMissing) {
			ctx.addIssue({
				path: [ "densityTemperature" ],
				code: z.ZodIssueCode.custom,
				message: "densityTemperature is required when densityKgM3 is provided.",
			});
		}
		if (
			values.densityKgM3Min != null &&
			values.densityKgM3Max != null &&
			tempMissing
		) {
			ctx.addIssue({
				path: [ "densityTemperature" ],
				code: z.ZodIssueCode.custom,
				message:
					"densityTemperature is required when densityKgM3Min and densityKgM3Max are provided.",
			});
		}
	}

	// Add validation conditionally when a product is saved directly as validated
	if (values.isValidated) {
		if (values.productNSTRGoodId == null) {
			ctx.addIssue({
				path: [ "productNSTRGoodId" ],
				code: z.ZodIssueCode.custom,
				message: intl.formatMessage({ id: "PRODUCT_NSTR_GOOD_REQUIRED" }),
			});
		}

		if (values.isDangerousGood) {
			const adnGood = values.adnGoodInput;

			if (!adnGood) {
				ctx.addIssue({
					path: [ "adnGoodInput" ],
					code: z.ZodIssueCode.custom,
					message: "Dangerous good details are required when product is validated.",
				});
			} else {
				const requiredFields: Array<keyof AdnGoodSchemaInput> = [
					"hazardCode",
					"cargoTankDesign",
					"cargoTankType",
					"openingPressureRelieveValve",
					"maxFillingDegreePercentage",
					"samplingType",
				];

				for (const field of requiredFields) {
					const v = adnGood[field] as unknown;

					const isEmpty =
						v == null ||
						(typeof v === "string" && v.trim().length === 0) ||
						(Array.isArray(v) && v.length === 0);

					const hasAsterisk = typeof v === "string" && v.includes("*");

					if (isEmpty || hasAsterisk) {
						ctx.addIssue({
							path: [ "adnGoodInput", field ],
							code: z.ZodIssueCode.custom,
							message: "This field is required and must be a valid value.",
						});
					}
				}
			}
		}
	}
});

export type ProductSchemaInput = z.input<typeof productSchema>;
export type ProductSchemaOutput = z.output<typeof productSchema>;

export type AdnGoodSchemaInput = z.input<typeof adnGoodSchema>;

